// src/components/TrafficSignMarker.js
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from 'react-dom';
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { signTypes } from "../data/fakeSignals";
import { Button, Col, Row, Spin, Image } from "antd";
import { MdDelete } from 'react-icons/md';
import { EnvironmentOutlined } from "@ant-design/icons";
import TrafficSignTooltip from './TrafficSignTooltip';
import "../styles/components/signal-fix.css";
import { GrEdit } from "react-icons/gr";
import { LuScan } from "react-icons/lu";
import { useTranslation } from 'react-i18next';
import { FaCube } from "react-icons/fa";

const TrafficSignMarker = ({ sign, onMarkerClick, isSelected, onView3D, onDelete }) => {
  const { t } = useTranslation();
  const [streetName, setStreetName] = useState("");
  const [loadingStreet, setLoadingStreet] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipPortal, setTooltipPortal] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const markerRef = useRef(); // reference to the marker element

  const normalizedType = sign.type
    .replace(/-/g, "-")
    .toLowerCase()
    .replace(/\//g, "_");
  const signType = signTypes[normalizedType] || {};
  // Create portal container for tooltip
  useEffect(() => {
    const portalDiv = document.createElement('div');
    portalDiv.id = `traffic-sign-tooltip-portal-${sign.id}`;
    document.body.appendChild(portalDiv);
    setTooltipPortal(portalDiv);

    return () => {
      if (document.body.contains(portalDiv)) {
        document.body.removeChild(portalDiv);
      }
    };
  }, [sign.id]);

  useEffect(() => {
    setImageLoading(true);
  }, [sign.image_url]);

  const handlePopupClose = (event) => {
    event?.stopPropagation();
    if (markerRef.current) {
      markerRef.current.closePopup();
    }
    setPreviewVisible(false);
    setShowPopup(false);
  };

  const handleMarkerClick = (event) => {
    if (event && event.containerPoint) {
      // Get the map container position
      const mapContainer = event.target._map.getContainer();
      const mapRect = mapContainer.getBoundingClientRect();

      // Calculate absolute position
      setTooltipPosition({
        x: mapRect.left + event.containerPoint.x,
        y: mapRect.top + event.containerPoint.y
      });
      // Toggle tooltip
      setShowTooltip(!showTooltip);
      setShowPopup(false);
    }
  };

  const handleEditClick = () => {
    setShowTooltip(false);
    setShowPopup(true);
    if (onMarkerClick) {
      onMarkerClick(sign);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTooltip && !event.target.closest('.traffic-sign-tooltip') && !event.target.closest('.traffic-sign-marker')) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTooltip]);
  const iconInner = signType.iconUrl
    ? `<img src="${signType.iconUrl}" style="width:20px;height:20px;object-fit:contain;display:block;" />`
    : `${signType.icon || "🚦"}`;

  // Check if sign is Segnaletica
  const isSegnaletica = sign.Segnaletica === true;

  const markerIcon = L.divIcon({
    className: "traffic-sign-marker",
    html: `
      <div class="traffic-sign-marker-icon ${
        isSelected ? "traffic-sign-marker-active" : ""
      } ${isSegnaletica ? "segnaletica-marker" : ""}" style="
        background-color: ${isSegnaletica ? "white" : "transparent"};
        border: ${isSegnaletica ? "2px solid #A1E340" : "none"};
;
        box-shadow: ${isSegnaletica ? "3px 3px 3px #A1E340" : "none"};
        border-radius: ${isSegnaletica ? "8px" : "0"};
        padding: ${isSegnaletica ? "10px" : "0"};
        color: inherit;">
        ${iconInner}
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });

  useEffect(() => {
    const fetchStreet = async () => {
      setLoadingStreet(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${sign.lat}&lon=${sign.lng}`
        );
        const data = await response.json();
        const roadName =
          data.address?.road ||
          data.address?.pedestrian ||
          data.display_name ||
          "Unknown street";
        setStreetName(roadName);
      } catch (error) {
        console.error("Error during reverse geocoding:", error);
        setStreetName("Unknown street");
      } finally {
        setLoadingStreet(false);
      }
    };
    if (isSelected) {
      fetchStreet();

      const timeout = setTimeout(() => {
        if (markerRef.current) {
          markerRef.current.closePopup();
        }
      }, 4000);

      return () => clearTimeout(timeout);
    }
  }, [isSelected, sign.lat, sign.lng]);

  // Ensure valid coordinates before rendering
  if (
    !sign?.lat ||
    !sign?.lng ||
    isNaN(Number(sign.lat)) ||
    isNaN(Number(sign.lng))
  ) {
    console.warn("TrafficSignMarker: Invalid coordinates", {
      lat: sign?.lat,
      lng: sign?.lng,
    });
    return null;
  }

  const position = [Number(sign.lat), Number(sign.lng)];

  return (
    <>
      <Marker
        position={position}
        icon={markerIcon}
        ref={markerRef}
        eventHandlers={{
          click: (e) => {
            e.originalEvent.stopPropagation();
            handleMarkerClick(e);
          }
        }}
      >
        {showPopup && (
          <Popup autoPan={true} style={{ width: "340px", padding: "20px 15px" }}>
        <div className="traffic-sign-popup">
          <h3>
            {" "}
            <b>{sign.type.replace("-", " ")}</b>{" "}
          </h3>
          {sign.image_url && (
            <div className="traffic-sign-image-container">
              <img
                src={sign.image_url}
                alt={sign.description}
                className="traffic-sign-image"
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
                style={{ opacity: imageLoading ? 0 : 1 }}
              />
              {imageLoading && (
                <div className="traffic-sign-image-loading">
                  <Spin size="large" />
                </div>
              )}
              <button
                type="button"
                className="traffic-sign-preview-button"
                onClick={() => setPreviewVisible(true)}
                disabled={imageLoading}
              >
                <LuScan size={18} />
              </button>
              <Image
                src={sign.image_url}
                alt={sign.description}
                preview={{
                  visible: previewVisible,
                  onVisibleChange: (visible) => setPreviewVisible(visible),
                }}
                style={{ display: "none" }}
              />
            </div>
          )}
          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <EnvironmentOutlined style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>Indirizzo</span>
              <br />
              <b>{loadingStreet ? <Spin size="small" /> : streetName}</b>
            </Col>
          </Row>

          <Row
            justify={"start"}
            align="middle"
            gutter={8}
            style={{ margin: "18px 0" }}
          >
            <Col>
              <EnvironmentOutlined style={{ color: "#A1E430" }} />
            </Col>
            <Col>
              <span>Coordinate</span>
              <br />
              <b>
                {sign.lat.toFixed(6)}, {sign.lng.toFixed(6)}
              </b>
            </Col>
          </Row>
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
          <Button
            color="green"
            style={{
              flex: 1,
              borderRadius: "25px",
              backgroundColor: "var(--primary-color)",
              color: "white",
            }}
            onClick={() => onMarkerClick(sign)}
          >
            <GrEdit />
            Modifica
          </Button>

          <Button
            danger
            style={{
              flex: 1,
              borderRadius: "25px",
              backgroundColor: "#ff4d4f",
              color: "white",
              border: "none"
            }}
            onClick={() => {
              handlePopupClose();
              if (onDelete) {
                onDelete(sign);
              }
            }}
          >
            <MdDelete />
            Elimina
          </Button>
        </div>
      </Popup>
        )}
      </Marker>

      {/* Render tooltip in portal */}
      {tooltipPortal && showTooltip && ReactDOM.createPortal(
        <TrafficSignTooltip
          sign={sign}
          onView3D={(signData) => {
            setShowTooltip(false);
            if (onView3D) {
              onView3D(signData);
            }
          }}
          onEdit={(signData) => {
            setShowTooltip(false);
            handleEditClick();
          }}
          onDelete={(signData) => {
            setShowTooltip(false);
            if (onDelete) {
              onDelete(signData);
            }
          }}
          onClose={() => setShowTooltip(false)}
          visible={true}
          position={tooltipPosition}
        />,
        tooltipPortal
      )}
    </>
  );
};

export default TrafficSignMarker;
